// s13b Детали визита (Mini-sprint 6-visits) — визуал по прототипу prototypes/index.html#s13b.
// Шапка без действий (без 3 точек), действия — строкой внизу: «Редактировать» + «Удалить».
// «Поделиться» не делаем (отдельный mini-sprint).

import type { ReactNode } from 'react';
import { useLocation, useRoute } from 'wouter';
import { ArrowLeft, User, MapPin, Pencil, Trash2, Eye } from 'lucide-react';

import { MobileFrame } from '@/components/layout/MobileFrame';
import { useToast } from '@/hooks/use-toast';
import { useVisit, useDeleteVisitMutation, useFetchVisitAttachmentUrl } from '@/api/hooks/visits';
import { formatVisitDateFull, formatFileSize } from '@/lib/visit-format';

export function VisitDetail() {
  const [, params] = useRoute('/visits/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const id = params?.id ? Number(params.id) : undefined;

  const { data: visit, isLoading, error } = useVisit(id);
  const deleteMut = useDeleteVisitMutation();
  const fetchUrlMut = useFetchVisitAttachmentUrl(visit?.id ?? 0);

  if (isLoading) return <Spinner />;
  if (error || !visit) return <NotFoundOrError onBack={() => setLocation('/visits')} />;

  async function handleDelete() {
    if (!window.confirm('Удалить визит? Действие необратимо.')) return;
    try {
      await deleteMut.mutateAsync(visit!.id);
      setLocation('/visits');
    } catch {
      toast({
        title: 'Не удалось удалить',
        description: 'Проверьте соединение и попробуйте ещё раз.',
        variant: 'destructive',
      });
    }
  }

  async function handleOpenAttachment(attachmentId: number) {
    // Окно открываем СИНХРОННО в клик-хендлере (сохраняем user-gesture — иначе на
    // iOS/Safari async window.open после await блокируется popup-блокером). Сначала
    // открываем about:blank, затем после signed_url проставляем location. opener=null
    // вместо noopener (с noopener window.open вернул бы null и нельзя было бы доставить URL).
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    try {
      const { signed_url } = await fetchUrlMut.mutateAsync(attachmentId);
      if (win) {
        win.location.href = signed_url;
      } else {
        // popup всё же заблокирован — пробуем прямой open как fallback.
        window.open(signed_url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      if (win) win.close();
      toast({
        title: 'Не удалось открыть файл',
        description: 'Попробуйте ещё раз.',
        variant: 'destructive',
      });
    }
  }

  const hasNotes = !!visit.notes?.trim();
  const attachments = visit.attachments;

  return (
    <MobileFrame>
      {/* Шапка — только назад + заголовок (без действий, по прототипу + п.1) */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-3 pb-3 pt-2">
        <button
          onClick={() => setLocation('/visits')}
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"
          aria-label="Назад"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 text-base font-semibold">Визит к врачу</div>
      </div>

      <main className="flex-1 overflow-y-auto px-5 py-5">
        {/* Hero: диагноз + дата с годом */}
        <h1 className="mb-1 text-hl-title font-bold leading-tight">{visit.diagnosis}</h1>
        <p className="mb-5 text-hl-secondary text-muted-foreground">{formatVisitDateFull(visit.visit_date)}</p>

        {/* Врач + Клиника — единый блок на серой подложке */}
        {(visit.doctor_name || visit.clinic_name) && (
          <div className="mb-5 space-y-3 rounded-surface border border-border bg-muted p-4">
            {visit.doctor_name && (
              <MetaRow icon={<User className="h-4 w-4" />} label="Врач" value={visit.doctor_name} />
            )}
            {visit.clinic_name && (
              <MetaRow icon={<MapPin className="h-4 w-4" />} label="Клиника" value={visit.clinic_name} />
            )}
          </div>
        )}

        {/* Комментарии — белый блок */}
        {hasNotes && (
          <div className="mb-5">
            <div className="mb-2 text-hl-small font-semibold uppercase tracking-wider text-muted-foreground">
              Комментарии
            </div>
            <div className="whitespace-pre-line rounded-surface border border-border bg-surface p-4 text-hl-secondary leading-relaxed text-foreground">
              {visit.notes}
            </div>
          </div>
        )}

        {/* Заключения и фото — список файлов, каждый открывается (иконка-глаз) */}
        {attachments.length > 0 && (
          <div className="mb-5">
            <div className="mb-2 text-hl-small font-semibold uppercase tracking-wider text-muted-foreground">
              Заключения и фото
            </div>
            <div className="space-y-2">
              {attachments.map((att) => (
                <button
                  key={att.id}
                  type="button"
                  onClick={() => handleOpenAttachment(att.id)}
                  disabled={fetchUrlMut.isPending}
                  className="flex w-full items-center gap-3 rounded-surface border border-border bg-surface p-3 text-left hover:border-primary disabled:opacity-50"
                >
                  <FileIcon mime={att.mime_type} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{att.original_filename}</div>
                    <div className="text-hl-small text-muted-foreground">{formatFileSize(att.size_bytes)}</div>
                  </div>
                  <Eye className="h-5 w-5 flex-shrink-0 text-primary-text" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Действия — строкой внизу: Редактировать (контур) + Удалить (красная) */}
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setLocation(`/visits/${visit.id}/edit`)}
            className="flex h-10 flex-1 items-center justify-center gap-2 rounded-control border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-muted"
          >
            <Pencil className="h-4 w-4" /> Редактировать
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMut.isPending}
            className="flex h-10 flex-1 items-center justify-center gap-2 rounded-control bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Удалить
          </button>
        </div>

        {/* Дисклеймер — жёлтый баннер, как в форме создания/редактирования */}
        <div className="rounded-surface border border-amber-200 bg-amber-50 p-3 text-hl-small leading-snug text-amber-900">
          <strong>HealthLab — это журнал, не врач.</strong> Мы не контролируем приём и не напоминаем.
          Следуйте инструкциям лечащего врача.
        </div>
      </main>
    </MobileFrame>
  );
}

function MetaRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-control bg-primary/10 text-primary-text">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-hl-small text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function FileIcon({ mime }: { mime: string }) {
  const isPdf = mime === 'application/pdf';
  return (
    <div
      className={`flex h-12 w-10 flex-shrink-0 items-center justify-center rounded-md ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}
    >
      <span className={`text-hl-small font-bold ${isPdf ? 'text-red-700' : 'text-blue-700'}`}>
        {isPdf ? 'PDF' : 'IMG'}
      </span>
    </div>
  );
}

function Spinner() {
  return (
    <MobileFrame>
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
      </div>
    </MobileFrame>
  );
}

function NotFoundOrError({ onBack }: { onBack: () => void }) {
  return (
    <MobileFrame>
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <p className="mb-3 text-sm text-muted-foreground">Визит не найден.</p>
        <button onClick={onBack} className="text-sm font-semibold text-primary-text">
          К списку визитов
        </button>
      </div>
    </MobileFrame>
  );
}
